import { NotificationReducer, NotificationType } from 'types/notification'
import { BlueText } from '../styles'
import { NotificationGuidance } from './NotificationGuidance'
import { IAuthenticationReducer } from 'types/autentication'
import { Control, FieldValues, UseFormWatch } from 'react-hook-form'
import { IVariableReducer } from 'types/variable'
import { variableListNumberPatientsInvolved } from '../helper'
import { probalityOccurenceInfoMapper } from 'pages/NotifyEvent/constant_text'
import { useSelector } from 'react-redux'
import { RootReducerState } from 'types/global'
import { getSubdamageLevel } from 'utils/subdamageEnum'
import { requiresSubDamageLevel } from 'utils/damage'

interface InfoWithoutInputsProps {
    notification: NotificationType
    auth: IAuthenticationReducer
    watch: UseFormWatch<FieldValues>
    control: Control<FieldValues>
    variableSelector?: IVariableReducer
}

export const InfoWithoutInputs = ({
    notification,
    auth,
    watch,
    control,
    variableSelector
}: InfoWithoutInputsProps) => {

    const notificationSelector: NotificationReducer = useSelector((state: RootReducerState) => state.notificationsReducer)

    function showNotificationGuidance() {
        if (notification?.u_image_risk.value?.includes('yes')) {
            return true
        }
    }

    const showMultiplierInput =
        notification?.u_number_patients_involved?.value ===
        variableListNumberPatientsInvolved(variableSelector)?.choices?.[2]
            ?.value && notification?.involves_patient?.value


    const getLabelByProbality = (probality: string) => {
        const item = probalityOccurenceInfoMapper.find((e) => e.probality === parseInt(probality));
        return item ? item.label : "-";
    };

    const getNeverEvent = () => {
        if (notificationSelector?.neverEvents && Array.isArray(notificationSelector?.neverEvents)) {
            const neverEventReferenceId = notificationSelector?.notificationById?.u_never_events_reference?.value
            const [neverEvent] = notificationSelector?.neverEvents?.filter((ne) => ne?.sys_id == neverEventReferenceId)
            return neverEvent?.u_description || "-";
        }
        return "-";
    }

    const getSentinelEvent = () => {
        if (notificationSelector?.neverEvents && Array.isArray(notificationSelector?.sentinelEvent)) {
            const sentinelEventId = notificationSelector?.notificationById?.u_sentinel_event_jci?.value
            const [sentinelEvent] = notificationSelector.sentinelEvent.filter( (se) => se?.sys_id === sentinelEventId);
            return sentinelEvent?.description || "-";
        }

        return "-";
    };

    const showSubDamageLevel = (() => {
        const notificationVersion = notification?.u_version?.value || process.env.REACT_APP_NOTIFICATION_VERSION
        const normalizedVersion = notificationVersion?.replace('v', '')
        const majorVersion = Number(normalizedVersion)

        return (
            requiresSubDamageLevel(notification?.damage_level?.value) &&
            Number.isFinite(majorVersion) &&
            majorVersion >= 4
        )
    })()

    return (
        <div className="flex flex-col gap-6">
            <div className="grid lg:grid-cols-3 grid-cols-2 gap-y-6 gap-x-4">
                <div
                    className={`${showNotificationGuidance() ? 'col-span-2' : 'col-span-3'} flex flex-col gap-6`}
                >
                    <div className="h-px bg-borderLine" />
                    <BlueText>
                        Informações complementares da Qualidade da Unidade
                    </BlueText>

                    <div className='grid grid-cols-2 gap-6'>
                        <InfoTexts
                            title="Tipo do incidente confirmado pela Qualidade da Unidade"
                            subtitle={
                                notification?.u_reclassified_incident_type
                                    ?.display_value || '-'
                            }
                        />
                        <InfoTexts
                            title="Classificação do incidente de acordo com a OMS - Organização Mundial da Saúde"
                            subtitle={
                                notification?.u_oms_classification?.display_value ||
                                '-'
                            }
                        />

                    </div>

                    <div className='grid grid-cols-3 gap-6'>
                        <InfoTexts
                            title="Frequência do evento na Unidade"
                            subtitle={getLabelByProbality(notification?.u_probability_occurence?.value)}
                        />

                        <InfoTexts
                            title="Evento com Risco de Imagem?"
                            subtitleClassNames="uppercase"
                            subtitle={
                                notification?.u_image_risk?.display_value || '-'
                            }
                        />

                        {showSubDamageLevel && (
                            <InfoTexts title="Grave ou Óbito"
                                subtitle={getSubdamageLevel(notification?.u_sub_damage_level?.value)?.subtitle ||'-'} />
                        )}
                    </div>

                    
                    <InfoTexts title="Never Events - ANVISA" subtitle={getNeverEvent()}/>

                    <InfoTexts title="Evento Sentinela - JCI" subtitle={(getSentinelEvent())} />
                    
                    <div className="h-px bg-borderLine" />
                </div>

                <NotificationGuidance
                    variableSelector={variableSelector}
                    control={control}
                    showNotificationGuidance={showNotificationGuidance}
                    authProfile={auth?.dataJwt?.profile}
                    notification={notification}
                    watch={watch}
                />
            </div>

            <BlueText>Informações da notificação</BlueText>

            <div className="flex gap-[72px]">
                <InfoTexts
                    title="Data da ocorrência"
                    subtitle={notification?.date_incident?.display_value || '-'}
                />
                <InfoTexts
                    title="Título do incidente"
                    subtitle={
                        notification?.incident_title?.display_value || '-'
                    }
                />
            </div>

            <div className="grid lg:grid-cols-4 md:grid-cols-2 grid-cols-1 gap-y-4">
                <InfoTexts
                    classNames="col-span-1"
                    title="Unidade"
                    subtitle={
                        notification?.location_incident?.display_value || '-'
                    }
                />
                <InfoTexts
                    classNames="col-span-1"
                    title="Setor notificado"
                    subtitle={
                        notification?.incident_sector?.display_value || '-'
                    }
                />
                <InfoTexts
                    classNames="col-span-1"
                    title="Turno"
                    subtitle={
                        notification?.shift_incident?.display_value || '-'
                    }
                />
                <InfoTexts
                    classNames="col-span-1"
                    title="Setor do notificante"
                    subtitle={
                        notification?.reporter_sector?.display_value || '-'
                    }
                />                        
            </div>

            <InfoTexts
                title="Descrição do incidente"
                subtitle={
                    notification?.incident_description?.display_value || '-'
                }
            />
            <InfoTexts
                title="Ação corretiva imediata"
                subtitle={
                    notification?.immediate_corrective_action?.display_value ||
                    '-'
                }
            />

            <div className="grid grid-cols-4">
                <InfoTexts
                    classNames="lg:col-span-1 col-span-2"
                    title="Classificação do Incidente"
                    subtitle={notification?.damage_level?.display_value || '-'}
                />
                <InfoTexts
                    classNames="lg:col-span-1 col-span-2"
                    title="Fator contribuinte"
                    subtitle={
                        notification?.u_categorized_contributing_factor
                            ?.display_value ||
                        '-' ||
                        '-'
                    }
                />
            </div>


            {showMultiplierInput ? (
                <div className="grid grid-cols-4">
                    <InfoTexts
                        classNames="lg:col-span-1 col-span-2"
                        title="Quantos pacientes estão relacionados"
                        subtitle={
                            notification?.u_number_patients_involved
                                ?.display_value || '-'
                        }
                    />
                    <InfoTexts
                        classNames="lg:col-span-1 col-span-2"
                        title="Multiplicador"
                        subtitle={
                            notification?.u_notification_multiplier?.display_value ||
                            '-' || '-'
                        }
                    />
                </div>
            ) : (
                <div className="grid lg:grid-cols-5 md:grid-cols-2 grid-cols-1 gap-y-4">
                    <InfoTexts
                        classNames={`col-span-1 ${notification?.patient_initials?.display_value?.length >=
                            30 && 'truncate'
                            }`}
                        title="Iniciais do nome do paciente"
                        subtitle={
                            notification?.patient_initials?.display_value?.length >=
                                30
                                ? notification?.patient_initials?.display_value.substring(
                                    0,
                                    30
                                )
                                : notification?.patient_initials?.display_value ||
                                '-'
                        }
                    />
                    <InfoTexts
                        classNames="col-span-1"
                        title="Data de nascimento"
                        subtitle={
                            notification?.patient_date_of_birth?.display_value ||
                            '-'
                        }
                    />
                    <InfoTexts
                        title="Gênero"
                        subtitle={
                            notification?.patient_gender?.display_value || '-'
                        }
                    />
                    <InfoTexts
                        classNames="col-span-1"
                        title="Tipo de documento"
                        subtitle={notification?.document_type?.display_value || '-'}
                    />
                    <InfoTexts
                        classNames={`col-span-1 ${notification?.document_number?.display_value?.length >=
                            30 && 'truncate'
                            }`}
                        title="Número do documento"
                        subtitle={
                            notification?.document_number?.display_value?.length >=
                                30
                                ? notification?.document_number?.display_value.substring(
                                    0,
                                    30
                                )
                                : notification?.document_number?.display_value ||
                                '-'
                        }
                    />
                </div>

            )}
        </div>
    )
}

interface InfoTextProps {
    title: string
    subtitle: string
    classNames?: string
    subtitleClassNames?: string
}
const InfoTexts = ({
    title,
    subtitle,
    classNames,
    subtitleClassNames
}: InfoTextProps) => {
    return (
        <div className={classNames}>
            <p className="text-xs text-darkGray/80">{title}</p>
            <p
                className={`font-semibold text-sm text-darkGray ${subtitleClassNames}`}
            >
                {subtitle}
            </p>
        </div>
    )
}
